import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_ROLES } from "../const";
import { useNavigate, Link } from 'react-router-dom';
import { showSuccessToast } from "../utils/toast";
import UserPermissionFields from '../components/UserPermissionFields';

export const RoleForm = ({ onSuccess, onCancel, onSubmittingChange }) => {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');

  const formik = useFormik({
    initialValues: { name: "", permissions: {} },
    validationSchema: Yup.object({
      name: Yup.string().required("Role name is required"),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.post(API_URL + END_POINT_ROLES, values, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to create role.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to create role.');
      } finally {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Row>
        <Col md={12} className="mb-3">
          <Form.Group controlId="role-name">
            <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              autoFocus
              disabled={formik.isSubmitting}
              {...formik.getFieldProps("name")}
              isInvalid={formik.touched.name && formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <UserPermissionFields formik={formik} title="Role Permissions" />
      <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          className="btn-quote-blue py-2 px-3 box-shedow-none outline-none"
          type="submit"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Form>
  );
};

export const CreateRoleForm = () => {
  const navigate = useNavigate();

  const handleSuccess = async (result) => {
    showSuccessToast(result.message)
    navigate('/roles');
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Add Role</h5>
          <Link to="/roles" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
            Back to Listing
          </Link>
        </div>
        <RoleForm onSuccess={handleSuccess} />
      </Card.Body>
    </Card>
  );
};
